import { useState, useEffect, useRef } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Sparkles, Loader2, Truck, MapPin, Check, X, Fuel, Package, Weight, Settings, Clock, Users } from 'lucide-react'
import toast from 'react-hot-toast'
import { api, formatMoney } from '@/lib/api'

const CORES = ['#2563eb', '#dc2626', '#16a34a', '#d97706', '#7c3aed', '#0891b2', '#db2777', '#65a30d']

let _leaflet: Promise<any> | null = null
function ensureLeaflet(): Promise<any> {
  if ((window as any).L) return Promise.resolve((window as any).L)
  if (_leaflet) return _leaflet
  _leaflet = new Promise((resolve, reject) => {
    const css = document.createElement('link'); css.rel = 'stylesheet'; css.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css'; document.head.appendChild(css)
    const s = document.createElement('script'); s.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js'; s.onload = () => resolve((window as any).L); s.onerror = reject; document.head.appendChild(s)
  })
  return _leaflet
}

function MapaRotas({ depot, cargas }: { depot: number[]; cargas: any[] }) {
  const ref = useRef<HTMLDivElement>(null)
  const mapRef = useRef<any>(null)
  useEffect(() => {
    if (!ref.current) return
    let cancel = false
    ensureLeaflet().then((L) => {
      if (cancel || !ref.current) return
      if (!mapRef.current) {
        mapRef.current = L.map(ref.current).setView(depot as any, 7)
        L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', { attribution: '© OpenStreetMap' }).addTo(mapRef.current)
      }
      const map = mapRef.current
      if (map._g) map.removeLayer(map._g)
      const g = L.layerGroup().addTo(map); map._g = g
      const all: any[] = [depot]
      L.marker(depot as any).bindPopup('🏭 Depósito').addTo(g)
      cargas.forEach((c, ci) => {
        const cor = CORES[ci % CORES.length]
        const pts = [depot, ...c.paradas.map((p: any) => [p.lat, p.lon])]
        L.polyline(pts as any, { color: cor, weight: 3, opacity: 0.7 }).addTo(g)
        c.paradas.forEach((p: any) => {
          all.push([p.lat, p.lon])
          L.circleMarker([p.lat, p.lon], { radius: 11, color: cor, weight: 2, fillColor: cor, fillOpacity: 0.85 })
            .bindTooltip(String(p.seq), { permanent: true, direction: 'center', className: 'seq-lbl' })
            .bindPopup(`<b>${p.seq}. ${p.cliente || 'Cliente'}</b><br/>${p.cidade || ''}/${p.uf || ''}<br/>Ped #${p.numero} · ${p.peso} kg · ${p.cubagem} m³`)
            .addTo(g)
        })
      })
      if (all.length > 1) map.fitBounds(all, { padding: [40, 40] })
      setTimeout(() => map.invalidateSize(), 250)
    })
    return () => { cancel = true }
  }, [depot, cargas])
  return <div ref={ref} className="rounded-xl border" style={{ height: '60vh', width: '100%' }} />
}

function sofiaHtml(t: string): string {
  const esc = (t || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
  return esc.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>').replace(/\n\n/g, '<br/><br/>').replace(/\n/g, '<br/>')
}

function Campo({ label, v, on, step = '0.01' }: { label: string; v: any; on: (x: number) => void; step?: string }) {
  return (
    <label className="block">
      <span className="block text-xs text-gray-500 mb-0.5">{label}</span>
      <input type="number" step={step} value={v ?? ''} onChange={e => on(e.target.value === '' ? 0 : parseFloat(e.target.value))} className="input w-full" />
    </label>
  )
}

export default function RoteirizacaoPage() {
  const qc = useQueryClient()
  const [sug, setSug] = useState<any>(null)
  const [veicSel, setVeicSel] = useState<Record<number, number>>({})
  const [showCfg, setShowCfg] = useState(false)
  const [cfgForm, setCfgForm] = useState<any>(null)
  const [explica, setExplica] = useState<any>(null)
  const [explicando, setExplicando] = useState(false)
  const pedirExplicacao = async (id: number) => {
    setExplica(null); setExplicando(true)
    try { setExplica((await api.post(`/roteirizacao/sugestoes/${id}/explicar`)).data) } catch { /* silencioso */ }
    finally { setExplicando(false) }
  }
  const { data: pend } = useQuery<any>({ queryKey: ['rot-pendentes'], queryFn: async () => (await api.get('/roteirizacao/pedidos-pendentes')).data })
  const { data: cfg } = useQuery<any>({ queryKey: ['rot-config'], queryFn: async () => (await api.get('/roteirizacao/config')).data })
  useEffect(() => { if (cfg && !cfgForm) setCfgForm(cfg) }, [cfg])
  const setC = (k: string, v: any) => setCfgForm((s: any) => ({ ...s, [k]: v }))
  const salvarCfg = useMutation({
    mutationFn: async () => (await api.put('/roteirizacao/config', cfgForm)).data,
    onSuccess: () => { toast.success('Configurações salvas'); qc.invalidateQueries({ queryKey: ['rot-config'] }); setShowCfg(false) },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro'),
  })

  const sugerir = useMutation({
    mutationFn: async () => (await api.post('/roteirizacao/sugerir', {})).data,
    onSuccess: (d) => { setSug(d); setVeicSel({}); toast.success(`Sofia montou ${d.resumo.n_veiculos} carga(s)`); pedirExplicacao(d.id) },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro ao roteirizar'),
  })
  const aceitar = useMutation({
    mutationFn: async () => (await api.post(`/roteirizacao/sugestoes/${sug.id}/aceitar`,
      { veiculos: sug.cargas.map((c: any, ci: number) => veicSel[ci] || c.veiculo_id) })).data,
    onSuccess: (d) => { toast.success(`${d.cargas_criadas.length} carga(s) geradas!`); setSug(null); qc.invalidateQueries({ queryKey: ['rot-pendentes'] }) },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro ao aceitar'),
  })

  const r = sug?.resumo
  return (
    <div className="p-4 space-y-4">
      <style>{`.seq-lbl{background:transparent;border:none;box-shadow:none;color:#fff;font-weight:700;font-size:11px}`}</style>
      <div className="flex items-center gap-2">
        <Sparkles className="text-violet-500" />
        <h1 className="text-xl font-bold">Roteirização — Sofia</h1>
      </div>

      <div className="bg-white border rounded-xl p-4 flex flex-wrap items-center gap-4">
        <div>
          <div className="text-xs text-gray-500">Pedidos a roteirizar</div>
          <div className="text-2xl font-bold">{pend?.total ?? '—'}</div>
          <div className="text-xs text-gray-400">{pend?.com_coordenada?.length ?? 0} com mapa · {pend?.sem_coordenada?.length ?? 0} sem cidade</div>
        </div>
        <div className="text-sm text-gray-600">Peso: <b>{Math.round(pend?.peso_total || 0)} kg</b> · Cubagem: <b>{(pend?.cubagem_total || 0).toFixed(1)} m³</b></div>
        <div className="ml-auto flex items-center gap-2">
          <button onClick={() => setShowCfg(s => !s)} className="flex items-center gap-1 border rounded-lg px-3 py-2 text-sm"><Settings size={15} /> Configurações</button>
          <button onClick={() => sugerir.mutate()} disabled={sugerir.isPending || !pend?.com_coordenada?.length}
            className="flex items-center gap-2 bg-violet-600 text-white rounded-lg px-4 py-2 font-medium disabled:opacity-50">
            {sugerir.isPending ? <Loader2 className="animate-spin" size={16} /> : <Sparkles size={16} />}
            {sugerir.isPending ? 'Sofia montando…' : 'Sofia, montar cargas'}
          </button>
        </div>
      </div>

      {showCfg && cfgForm && (
        <div className="bg-white border rounded-xl p-4">
          <div className="font-semibold mb-3 flex items-center gap-1.5"><Settings size={15} /> Configurações de custo e tempo</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Campo label="Preço do litro (R$)" v={cfgForm.preco_litro} on={(x: number) => setC('preco_litro', x)} step="0.01" />
            <Campo label="Diária motorista (R$)" v={cfgForm.diaria_motorista} on={(x: number) => setC('diaria_motorista', x)} />
            <Campo label="Qtd ajudantes" v={cfgForm.qtd_ajudantes} on={(x: number) => setC('qtd_ajudantes', x)} step="1" />
            <Campo label="Diária ajudante (R$)" v={cfgForm.diaria_ajudante} on={(x: number) => setC('diaria_ajudante', x)} />
            <Campo label="Velocidade média (km/h)" v={cfgForm.velocidade_media_kmh} on={(x: number) => setC('velocidade_media_kmh', x)} />
            <Campo label="Min. por parada" v={cfgForm.minutos_por_parada} on={(x: number) => setC('minutos_por_parada', x)} step="1" />
            <Campo label="Jornada (h/dia)" v={cfgForm.jornada_horas_dia} on={(x: number) => setC('jornada_horas_dia', x)} step="0.5" />
            <Campo label="Custo máx. p/ valer (%)" v={cfgForm.custo_max_pct} on={(x: number) => setC('custo_max_pct', x)} />
          </div>
          <div className="text-xs text-gray-400 mt-2">Diária do motorista + ajudantes entram no custo da rota multiplicados pelos dias estimados.</div>
          <div className="flex justify-end mt-3"><button onClick={() => salvarCfg.mutate()} disabled={salvarCfg.isPending} className="bg-emerald-600 text-white rounded-lg px-4 py-2 text-sm">Salvar configurações</button></div>
        </div>
      )}

      {sug && r && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[
              { l: 'Veículos', v: r.n_veiculos, i: <Truck size={15} /> },
              { l: 'Entregas', v: r.n_paradas, i: <MapPin size={15} /> },
              { l: 'Km total', v: `${r.km_total} km`, i: <MapPin size={15} /> },
              { l: 'Tempo', v: `${r.tempo_horas}h${r.dias_max > 1 ? ` (~${r.dias_max}d)` : ''}`, i: <Clock size={15} /> },
              { l: 'Receita', v: formatMoney(r.valor_total), i: <Package size={15} /> },
              { l: 'Combustível', v: formatMoney(r.combustivel_rs), i: <Fuel size={15} /> },
              { l: 'Mão de obra', v: formatMoney(r.mao_obra_rs), i: <Users size={15} /> },
              { l: 'Custo / receita', v: r.custo_pct != null ? `${r.custo_pct}%` : '—', i: <Weight size={15} />, alerta: r.custo_pct != null && r.custo_pct > (cfg?.custo_max_pct ?? 15) },
            ].map((k: any, i) => (
              <div key={i} className="bg-white border rounded-xl p-3">
                <div className="flex items-center gap-1.5 text-xs text-gray-500">{k.i}{k.l}</div>
                <div className={`text-xl font-bold mt-1 ${k.alerta ? 'text-red-600' : ''}`}>{k.v}</div>
              </div>
            ))}
          </div>
          {r.rotas_nao_valem > 0 && (
            <div className="text-sm bg-amber-50 border border-amber-200 text-amber-800 rounded-lg px-3 py-2">
              ⚠️ {r.rotas_nao_valem} rota(s) com custo alto vs receita (acima de 15%) — vale revisar se compensam.
            </div>
          )}

          {(explicando || explica) && (
            <div className="bg-violet-50 border border-violet-200 rounded-xl p-4">
              <div className="flex items-center gap-2 font-semibold text-violet-700 mb-2">
                <Sparkles size={16} /> Sofia explica{explica?.fonte === 'resumo' ? ' (resumo automático — configure a IA p/ texto completo)' : ''}
              </div>
              {explicando
                ? <div className="flex items-center gap-2 text-sm text-violet-600"><Loader2 className="animate-spin" size={14} /> Sofia está analisando a melhor rota…</div>
                : <div className="text-sm text-gray-700 leading-relaxed" dangerouslySetInnerHTML={{ __html: sofiaHtml(explica.explicacao) }} />}
            </div>
          )}

          <div className="grid md:grid-cols-3 gap-4">
            <div className="md:col-span-2"><MapaRotas depot={sug.depot} cargas={sug.cargas} /></div>
            <div className="space-y-3 max-h-[60vh] overflow-auto">
              {sug.cargas.map((c: any, ci: number) => (
                <div key={ci} className="bg-white border rounded-xl p-3" style={{ borderLeftColor: CORES[ci % CORES.length], borderLeftWidth: 4 }}>
                  <div className="flex items-center justify-between gap-2">
                    <div className="font-semibold flex items-center gap-1.5 min-w-0">
                      <Truck size={15} className="shrink-0" />
                      <select value={veicSel[ci] || c.veiculo_id} onChange={e => setVeicSel(s => ({ ...s, [ci]: Number(e.target.value) }))}
                        className="border rounded px-1 py-0.5 text-sm font-semibold max-w-full">
                        <option value={c.veiculo_id}>{c.placa} ⭐ recomendado</option>
                        {c.alternativas?.map((a: any) => (
                          <option key={a.veiculo_id} value={a.veiculo_id}>{a.placa} — {formatMoney(a.combustivel_rs)}{a.ocupacao_peso != null ? ` · ${a.ocupacao_peso}%` : ''}</option>
                        ))}
                      </select>
                    </div>
                    <div className="text-xs text-gray-500 shrink-0">{c.paradas.length} entregas · {c.km} km</div>
                  </div>
                  <div className="flex gap-3 text-xs mt-2 flex-wrap">
                    <span className="flex items-center gap-1"><Weight size={12} /> {Math.round(c.peso_total)} kg {c.ocupacao_peso != null && <b className={c.ocupacao_peso > 100 ? 'text-red-600' : 'text-emerald-600'}>({c.ocupacao_peso}%)</b>}</span>
                    <span className="flex items-center gap-1"><Package size={12} /> {c.cubagem_total} m³ {c.ocupacao_cubagem != null && <b className={c.ocupacao_cubagem > 100 ? 'text-red-600' : 'text-emerald-600'}>({c.ocupacao_cubagem}%)</b>}</span>
                    <span className="flex items-center gap-1"><Fuel size={12} /> {formatMoney(c.combustivel_rs)}</span>
                    <span className="flex items-center gap-1"><Clock size={12} /> {c.tempo_horas}h{c.dias > 1 ? ` · ${c.dias}d` : ''}</span>
                    {c.mao_obra_rs > 0 && <span className="flex items-center gap-1"><Users size={12} /> {formatMoney(c.mao_obra_rs)}</span>}
                  </div>
                  <div className="flex items-center justify-between text-xs mt-2 pt-2 border-t">
                    <span>Receita <b>{formatMoney(c.valor_total)}</b> · custo <b className={c.custo_pct != null && c.custo_pct > (cfg?.custo_max_pct ?? 15) ? 'text-red-600' : ''}>{formatMoney(c.custo_total)}{c.custo_pct != null ? ` (${c.custo_pct}%)` : ''}</b></span>
                    <span className={`px-2 py-0.5 rounded-full font-medium ${c.vale_pena ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>{c.vale_pena ? '✅ Vale a pena' : '⚠️ Rever rota'}</span>
                  </div>
                  <ol className="text-xs text-gray-600 mt-2 space-y-0.5 list-decimal list-inside">
                    {c.paradas.map((p: any) => <li key={p.pedido_id}>{p.cliente || `Ped #${p.numero}`} <span className="text-gray-400">— {p.cidade}/{p.uf}</span></li>)}
                  </ol>
                </div>
              ))}
              {sug.sem_coordenada?.length > 0 && (
                <div className="text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded-lg p-2">
                  ⚠ {sug.sem_coordenada.length} pedidos sem cidade localizada ficaram de fora do mapa.
                </div>
              )}
            </div>
          </div>

          <div className="flex justify-end gap-2">
            <button onClick={() => setSug(null)} className="px-4 py-2 rounded-lg border">Descartar</button>
            <button onClick={() => aceitar.mutate()} disabled={aceitar.isPending}
              className="flex items-center gap-2 bg-emerald-600 text-white rounded-lg px-4 py-2 font-medium disabled:opacity-50">
              <Check size={16} /> Aceitar e gerar cargas
            </button>
          </div>
        </>
      )}
    </div>
  )
}
